import { useEffect, useState } from 'react';
import ChevronDown from 'svg/ChevronDown';
import ChevronUp from 'svg/ChevronUp';
import InfoAlert from 'components/InfoAlert';
import classNames from 'styles/utils/classNames';
import SlotChip from './SlotChip';
import { BookingSlot } from './types';
import { getSelectableSlotIds } from './utils';

interface Props {
  slots: BookingSlot[];
  selectedSlotIds: string[];
  onSlotSelect: (slotId: string) => void;
  /** Shows the minimum-playing-time alert when it exceeds one increment. */
  minimumMinutes: number;
  slotMinutes: number;
  className?: string;
  /** Desktop modal lays the same grid out in four columns. */
  columns?: 3 | 4;
}

const COLUMN_CLASSES: Record<NonNullable<Props['columns']>, string> = {
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

/**
 * The bookable-times grid.
 *
 * Slots past midnight are split out behind a "Late hours" toggle: clubs that run
 * until 6am would otherwise push the court list a full screen down.
 */
export default function SlotGrid({
  slots,
  selectedSlotIds,
  onSlotSelect,
  minimumMinutes,
  slotMinutes,
  className,
  columns = 3,
}: Props) {
  const [isLateHoursOpen, setIsLateHoursOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Keep today’s grid in sync as slots pass while the booking panel stays open.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const selectableIds = getSelectableSlotIds(slots, selectedSlotIds);
  const regularSlots = slots.filter((slot) => !slot.isLateHours);
  const lateSlots = slots.filter((slot) => slot.isLateHours);

  const renderSlot = (slot: BookingSlot) => (
    <SlotChip
      key={slot.id}
      slot={slot}
      isSelected={selectedSlotIds.includes(slot.id)}
      isSelectable={selectableIds.has(slot.id)}
      isPast={new Date(slot.startAt).getTime() <= now}
      onSelect={onSlotSelect}
    />
  );

  const gridClasses = classNames('grid gap-x-ds-md gap-y-ds-md', COLUMN_CLASSES[columns]);

  return (
    <div className={classNames('flex flex-col gap-ds-lg', className)}>
      {minimumMinutes > slotMinutes && (
        <InfoAlert variant="brand" desc={`Minimum playing time is ${minimumMinutes / 60}h`} />
      )}

      <div className={gridClasses}>{regularSlots.map(renderSlot)}</div>

      {lateSlots.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setIsLateHoursOpen((isOpen) => !isOpen)}
            aria-expanded={isLateHoursOpen}
            className="typography-product-button-label-large flex items-center justify-end gap-1.5 self-end py-ds-sm text-color-text-brand"
          >
            Late hours
            {isLateHoursOpen ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
          {isLateHoursOpen && <div className={gridClasses}>{lateSlots.map(renderSlot)}</div>}
        </>
      )}
    </div>
  );
}
